import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Card, Button, Space, Spin, Tag, Modal, Form, Input, Select, Tabs, Table, Statistic, Row, Col } from 'antd';
import { ArrowLeftOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '../../components/common/PageHeader';
import { ErrorResult } from '../../components/common/ErrorResult';
import {
  useAdminUserDetail,
  useUpdateAdminRole,
  useSuspendAdmin,
  useActivateAdmin,
  useRemoveAdmin,
  useRevokeAdminSession,
} from '../../hooks/useAdminUsers';
import { ADMIN_ROLE_LABELS } from '../../lib/constants';
import type { AdminUserActivityItem, AdminSessionItem } from '../../types/adminUsers';
import dayjs from 'dayjs';

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'red',
  finance_admin: 'blue',
  support_agent: 'green',
};

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: admin, isLoading, isError, refetch } = useAdminUserDetail(id!);
  const updateRole = useUpdateAdminRole();
  const suspendAdmin = useSuspendAdmin();
  const activateAdmin = useActivateAdmin();
  const removeAdmin = useRemoveAdmin();
  const revokeSession = useRevokeAdminSession();
  const [modal, setModal] = useState<string | null>(null);
  const [form] = Form.useForm();

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (isError || !admin) return <ErrorResult onRetry={refetch} />;

  const handleAction = () => {
    form.validateFields().then((values) => {
      if (modal === 'change_role') {
        updateRole.mutate({ id: id!, data: { admin_role: values.admin_role } }, { onSuccess: () => { setModal(null); form.resetFields(); refetch(); } });
      } else if (modal === 'suspend') {
        suspendAdmin.mutate({ id: id!, data: { reason: values.reason, revoke_sessions: true } }, { onSuccess: () => { setModal(null); form.resetFields(); refetch(); } });
      } else if (modal === 'revoke_session') {
        revokeSession.mutate({ adminId: id!, sessionId: values.session_id, data: { reason: values.reason } }, { onSuccess: () => { setModal(null); form.resetFields(); refetch(); } });
      }
    });
  };

  const handleRemove = () => {
    Modal.confirm({
      title: 'Remove Admin Privileges',
      icon: <ExclamationCircleOutlined />,
      content: `This will remove admin privileges from ${admin.email}. They will lose all admin access immediately.`,
      okText: 'Remove Admin',
      okType: 'danger',
      onOk: () => removeAdmin.mutate(id!, { onSuccess: () => navigate('/system/admin-users') }),
    });
  };

  const activityColumns: ColumnsType<AdminUserActivityItem> = [
    { title: 'Action', dataIndex: 'action', key: 'action', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Target', dataIndex: 'target_type', key: 'target', render: (type: string, r) => `${type}${r.target_identifier ? ': ' + r.target_identifier : ''}` },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip', render: (v: string | null) => v || '-' },
    { title: 'When', dataIndex: 'created_at', key: 'when', render: (v: string) => dayjs(v).format('MMM D, HH:mm:ss') },
  ];

  const sessionColumns: ColumnsType<AdminSessionItem> = [
    { title: 'IP', dataIndex: 'ip_address', key: 'ip', render: (v: string | null) => v || '-' },
    { title: 'User Agent', dataIndex: 'user_agent', key: 'ua', ellipsis: true, width: 250, render: (v: string | null) => v || '-' },
    { title: 'Location', dataIndex: 'location', key: 'loc', render: (v: string | null) => v || '-' },
    { title: 'Created', dataIndex: 'created_at', key: 'created', render: (v: string) => dayjs(v).format('MMM D, HH:mm') },
    {
      title: 'Status', key: 'status',
      render: (_, r) => {
        if (r.revoked_at) return <Tag color="red">Revoked</Tag>;
        if (dayjs(r.expires_at).isBefore(dayjs())) return <Tag color="default">Expired</Tag>;
        return <Tag color="green">Active</Tag>;
      },
    },
    {
      title: '', key: 'actions', width: 100,
      render: (_, r) => {
        if (r.revoked_at || dayjs(r.expires_at).isBefore(dayjs())) return null;
        return (
          <Button
            size="small"
            danger
            onClick={() => { form.setFieldsValue({ session_id: r.id }); setModal('revoke_session'); }}
          >
            Revoke
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={admin.full_name || admin.email}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/system/admin-users')}>Back</Button>
            <Tag color={ROLE_COLORS[admin.admin_role]}>{ADMIN_ROLE_LABELS[admin.admin_role] || admin.admin_role}</Tag>
            <Tag color={admin.is_active ? 'green' : 'red'}>{admin.is_active ? 'Active' : 'Suspended'}</Tag>
          </Space>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Button type="primary" onClick={() => { form.setFieldsValue({ admin_role: admin.admin_role }); setModal('change_role'); }}>Change Role</Button>
          {admin.is_active
            ? <Button danger onClick={() => setModal('suspend')}>Suspend</Button>
            : <Button onClick={() => activateAdmin.mutate(id!, { onSuccess: () => refetch() })}>Activate</Button>
          }
          <Button danger type="dashed" onClick={handleRemove}>Remove Admin</Button>
        </Space>
      </Card>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <Card>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={6}><Statistic title="Total Actions" value={admin.stats.total_actions} /></Col>
                  <Col span={6}><Statistic title="Last 30 Days" value={admin.stats.actions_last_30_days} /></Col>
                  <Col span={6}><Statistic title="Total Sessions" value={admin.stats.total_sessions} /></Col>
                  <Col span={6}><Statistic title="Active Sessions" value={admin.stats.active_sessions} /></Col>
                </Row>
                <Descriptions column={2}>
                  <Descriptions.Item label="Email">{admin.email}</Descriptions.Item>
                  <Descriptions.Item label="Role"><Tag color={ROLE_COLORS[admin.admin_role]}>{ADMIN_ROLE_LABELS[admin.admin_role]}</Tag></Descriptions.Item>
                  <Descriptions.Item label="2FA">{admin.admin_2fa_enabled ? <Tag color="green">Enabled</Tag> : <Tag color="orange">Not Set</Tag>}</Descriptions.Item>
                  <Descriptions.Item label="Status">{admin.is_active ? <Tag color="green">Active</Tag> : <Tag color="red">Suspended</Tag>}</Descriptions.Item>
                  <Descriptions.Item label="Last Login">{admin.last_login_at ? dayjs(admin.last_login_at).format('MMM D, YYYY HH:mm') : 'Never'}</Descriptions.Item>
                  <Descriptions.Item label="Created">{dayjs(admin.created_at).format('MMM D, YYYY')}</Descriptions.Item>
                  <Descriptions.Item label="Created By">{admin.created_by_email || '-'}</Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'activity',
            label: `Activity (${admin.recent_activity.length})`,
            children: <Table columns={activityColumns} dataSource={admin.recent_activity} rowKey="id" pagination={{ pageSize: 20 }} size="small" />,
          },
          {
            key: 'sessions',
            label: `Sessions (${admin.sessions.length})`,
            children: <Table columns={sessionColumns} dataSource={admin.sessions} rowKey="id" pagination={{ pageSize: 20 }} size="small" />,
          },
        ]}
      />

      <Modal
        title={modal === 'change_role' ? 'Change Role' : modal === 'suspend' ? 'Suspend Admin' : 'Revoke Session'}
        open={!!modal}
        onOk={handleAction}
        onCancel={() => { setModal(null); form.resetFields(); }}
        okText="Confirm"
        okButtonProps={{ danger: modal !== 'change_role' }}
      >
        <Form form={form} layout="vertical">
          {modal === 'change_role' && (
            <Form.Item name="admin_role" label="New Role" rules={[{ required: true }]}>
              <Select
                options={[
                  { label: 'Super Admin', value: 'super_admin' },
                  { label: 'Finance Admin', value: 'finance_admin' },
                  { label: 'Support Agent', value: 'support_agent' },
                ]}
              />
            </Form.Item>
          )}
          {(modal === 'suspend' || modal === 'revoke_session') && (
            <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Reason required' }]}>
              <Input.TextArea rows={2} />
            </Form.Item>
          )}
          {modal === 'revoke_session' && <Form.Item name="session_id" hidden><Input /></Form.Item>}
        </Form>
      </Modal>
    </>
  );
}
